import { useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/Popover';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { Employee, BehavioralNoteType } from '@/types';
import { Smile, Meh, Frown, History, ChevronLeft } from 'lucide-react';

const FACE_CONFIG: Record<
  BehavioralNoteType,
  { label: string; question: string; icon: typeof Smile; colorClass: string }
> = {
  positive: {
    label: 'Bien',
    question: '¿Qué hizo bien?',
    icon: Smile,
    colorClass: 'text-green-600 dark:text-green-400',
  },
  neutral: {
    label: 'Mejorar',
    question: '¿En qué podría mejorar?',
    icon: Meh,
    colorClass: 'text-amber-600 dark:text-amber-400',
  },
  negative: {
    label: 'Incidente',
    question: 'Registra el incidente que requiere atención',
    icon: Frown,
    colorClass: 'text-red-600 dark:text-red-400',
  },
};

export interface BitacoraEntryPopoverProps {
  employee: Employee;
  onSaved?: () => void;
  children: React.ReactNode;
}

export function BitacoraEntryPopover({ employee, onSaved, children }: BitacoraEntryPopoverProps) {
  const { appUser, canWriteCompanyData } = useAuth();
  const [step, setStep] = useState<'faces' | 'form'>('faces');
  const [selectedType, setSelectedType] = useState<BehavioralNoteType | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const companyId = appUser?.companyId ?? null;
  const employeeName = `${employee.firstName} ${employee.lastName}`.trim();

  const handleFaceClick = (type: BehavioralNoteType) => {
    if (!canWriteCompanyData) return;
    setSelectedType(type);
    setNote('');
    setStep('form');
  };

  const handleBack = () => {
    setStep('faces');
    setSelectedType(null);
    setNote('');
  };

  const handleSave = async () => {
    if (!companyId || !selectedType || !note.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db, 'behavioral_notes'), {
        companyId,
        employeeId: employee.id,
        employeeName,
        department: employee.department ?? null,
        type: selectedType,
        note: note.trim(),
        createdAt: new Date().toISOString(),
        createdByUid: appUser?.uid ?? '',
      });
      setStep('faces');
      setSelectedType(null);
      setNote('');
      onSaved?.();
    } catch (err) {
      console.error('Error al guardar nota:', err);
      window.alert('Error al guardar la nota. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="inline-block">{children}</div>
      </PopoverTrigger>
      <PopoverContent className="min-w-[280px] p-0" id={`bitacora-popover-${employee.id}`}>
        {step === 'faces' ? (
          <div className="p-3">
            {canWriteCompanyData ? (
              <>
                <p className="mb-3 text-sm font-medium text-muted-foreground">Registrar seguimiento</p>
                <div className="flex gap-2">
                  {(Object.entries(FACE_CONFIG) as [BehavioralNoteType, (typeof FACE_CONFIG)[BehavioralNoteType]][]).map(
                    ([type, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => handleFaceClick(type)}
                          className={cn(
                            'flex flex-1 flex-col items-center gap-1 rounded-lg border p-3 transition-colors hover:bg-muted/50',
                            config.colorClass
                          )}
                          title={config.label}
                        >
                          <Icon className="h-8 w-8" />
                          <span className="text-xs font-medium">{config.label}</span>
                        </button>
                      );
                    }
                  )}
                </div>
              </>
            ) : (
              <p className="mb-3 text-sm text-muted-foreground">
                Tu rol es solo lectura. Puedes consultar el historial, pero no registrar notas.
              </p>
            )}
            <Link
              to={`/app/bitacora/${employee.id}`}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border-t pt-3 text-sm text-muted-foreground hover:text-foreground"
            >
              <History className="h-4 w-4" />
              Ver historial
            </Link>
          </div>
        ) : (
          <div className="p-3">
            {selectedType && (
              <>
                <button
                  type="button"
                  onClick={handleBack}
                  className="mb-2 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Volver
                </button>
                <p className="mb-2 text-sm font-medium">{FACE_CONFIG[selectedType].question}</p>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Escribe una nota breve..."
                  rows={3}
                  className="mb-3 w-full resize-none rounded-md border bg-background px-3 py-2 text-sm"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={handleBack}>
                    Cancelar
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={!note.trim() || saving}>
                    {saving ? 'Guardando…' : 'Guardar'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
